(ns common.lp-report.export-permissions
  "Shared, pure helpers for LP-report export permissions (which formats a
  reporting contact may download). Stored as EDN on :lp-report/export-permissions:

    {:default   {:reporting-contact {:pdf true :xlsx true :json false}}
     :overrides {\"<investor-key>\" {:reporting-contact {...}}}}

  An investor absent from :overrides inherits :default. Used by the LP-report
  editor FE (render/dirty/normalize), the save endpoint, and LP-portal export
  enforcement (resolve/allow)."
  (:require
   [common.util :as cu]))

(def formats
  "Export formats, in display order. :xls covers the xlsx/Excel export."
  [:pdf :xls :json])

(def default-permissions
  "Baseline format permissions used when a report has no stored value, and to
  fill any missing format key."
  {:pdf true
   :xls true
   :json false})

(def FormatPerms
  [:map {:closed true}
   [:pdf boolean?]
   [:xls boolean?]
   [:json boolean?]])

(def RolePerms
  [:map {:closed true}
   [:reporting-contact FormatPerms]])

(def ExportPermissions
  [:map {:closed true}
   [:default RolePerms]
   [:overrides [:map-of :string RolePerms]]])

(def UpdateExportPermissionsRequest
  [:map {:closed true}
   [:report-id int?]
   [:permissions ExportPermissions]])

(def UpdateExportPermissionsResponse
  [:map {:closed true}
   [:report-id int?]
   ;; The stored, normalized permissions (overrides keyed by investor-key) so
   ;; the FE can update the report field directly and clear its dirty state.
   [:permissions ExportPermissions]])

(defn investor-key
  "Stable per-report key for an investor: external-id when present, else the
  normalized investor-name. Mirrors how imports and accessible-commitments
  match investors, so keys line up across re-import and report inheritance."
  [{:keys [external-id investor-name]}]
  (or external-id
      (cu/str-trim-lcase investor-name)))

(defn role-formats
  "Full {:pdf b :xlsx b :json b} for a role's perms map, filling missing keys
  from default-permissions."
  [role-perms]
  (merge default-permissions
         (:reporting-contact role-perms)))

(defn resolve-formats
  "Effective format permissions for one investor on a report: the investor's
  override when present, else the report default. Always returns a full
  {:pdf b :xlsx b :json b} map."
  [export-permissions inv-key]
  (let [ep (or export-permissions {})]
    (role-formats (or (get-in ep [:overrides inv-key])
                      (:default ep)))))

(defn format-allowed?
  [export-permissions inv-key fmt]
  (boolean (get (resolve-formats export-permissions inv-key) fmt)))

(defn any-allows?
  "Whether any of the investor keys permits fmt. Used for report-wide exports
  (e.g. PDF) where a contact may map to several commitments."
  [export-permissions inv-keys fmt]
  (boolean (some (fn [k]
                   (format-allowed? export-permissions k fmt))
                 inv-keys)))

(defn normalize
  "Canonical form for dirty-checking and persistence:
  - :default filled to full explicit booleans
  - :overrides limited to investors whose full formats differ from the default
    (overrides equal to the default collapse to inherit)."
  [export-permissions]
  (let [default-role {:reporting-contact (role-formats (:default export-permissions))}]
    {:default   default-role
     :overrides (->> (:overrides export-permissions)
                     (keep (fn [[k role-perms]]
                             (let [full {:reporting-contact (role-formats role-perms)}]
                               (when (not= full default-role)
                                 [k full]))))
                     (into {}))}))
